import { useState } from "react"
import moment from "moment"
import { BedDouble, Flag, Plane, Car, TrainFront, BadgePercent, Image as ImagePlaceholderIcon } from "lucide-react"

import { ROOM_TYPES } from "../../../../utils/exports/room-types"
import { BOARD_TYPES } from "../../../../utils/exports/board-types"
import { LUGGAGE_OPTIONS } from "./flight-transfer-modal"
import AIRPORTS from "./data/airports.json"

/**
 * Shared itinerary timeline.
 *
 * The "New Quote" builder and the public quote page both render the same
 * client-facing itinerary (a day-grouped timeline that mirrors the /itinerary
 * "manage my booking" portal). The node-building logic and the card components
 * live here so the two stay byte-identical: the builder feeds them a live
 * basket, the public page feeds them the basket persisted on the published
 * quote. Nothing here reads cost / margin data - it is purely presentational.
 */

// Client-facing wording for the round card. A round's `buggies` can be a legacy
// string ("none"/"included"/"local") or the structured object the round editor
// now writes ({ enabled, status, currency, quantity, price }).
export const BUGGY_ITINERARY_LABELS = {
	included: "Buggies included",
	local: "Buggies reserved for you to pay locally at the course",
}

// Fold both the legacy string and the structured object into one shape so the
// card (and the costing engine in new-quote) read buggies the same way.
export const normaliseBuggies = (raw) => {
	const defaults = { enabled: false, status: "included", currency: "gbp", quantity: "", price: "" }
	if (typeof raw === "string") {
		if (raw === "included") return { ...defaults, enabled: true, status: "included" }
		if (raw === "local") return { ...defaults, enabled: true, status: "local" }
		return { ...defaults }
	}
	if (raw && typeof raw === "object") return { ...defaults, ...raw }
	return { ...defaults }
}

// A charged buggy is part of the quoted price, so the client sees it as included;
// only a "pay locally" buggy gets the separate wording. Disabled -> no line.
const buggyLabel = (raw) => {
	const buggies = normaliseBuggies(raw)
	if (!buggies.enabled) return ""
	return BUGGY_ITINERARY_LABELS[buggies.status === "local" ? "local" : "included"]
}

// Airline logo by IATA code (kiwi's public logo CDN); blank when no code.
const carrierLogoUrl = (code) => (code ? `https://images.kiwi.com/airlines/64/${code.trim().toUpperCase()}.png` : "")

// Human duration between two datetimes, e.g. "2h 30m".
const flightDuration = (depMillis, arrMillis) => {
	if (!depMillis || !arrMillis || arrMillis <= depMillis) return ""
	const mins = Math.round((arrMillis - depMillis) / 60000)
	const h = Math.floor(mins / 60)
	const m = mins % 60
	return `${h}h${m ? ` ${m}m` : ""}`
}

/**
 * Collapse repeat stays at the same hotel on the same check-in day into one
 * card. Agents sometimes add a hotel twice to price one continuous stay on two
 * windows - e.g. a 3-night package plus a 1-night supplement package - so the
 * client itinerary should read as a single stay: nights summed, checkout pushed
 * out accordingly. Other node kinds (rounds, flights, transfers) pass through
 * untouched, and stays whose check-in differs are left as separate cards.
 */
const mergeHotelStays = (nodes) => {
	const out = []
	const indexByKey = new Map()
	nodes.forEach((node) => {
		if (node.kind !== "hotel") {
			out.push(node)
			return
		}
		// Same hotel (cms id, or title when unresolved) + same check-in day.
		const mergeKey = `${node.cmsId || node.name}|${node.day}`
		const existingIndex = indexByKey.get(mergeKey)
		if (existingIndex === undefined) {
			indexByKey.set(mergeKey, out.length)
			// Clone the rooms array so we don't mutate the source node when merging.
			out.push({ ...node, rooms: [...node.rooms] })
			return
		}
		const existing = out[existingIndex]
		existing.nights += node.nights
		existing.rounds += node.rounds
		// One continuous stay, so a board / room shared by both rows shows once;
		// a genuinely different one is appended rather than dropped.
		if (node.board && existing.board) {
			if (!existing.board.split(", ").includes(node.board)) existing.board = `${existing.board}, ${node.board}`
		} else if (node.board) {
			existing.board = node.board
		}
		node.rooms.forEach((room) => {
			if (!existing.rooms.includes(room)) existing.rooms.push(room)
		})
	})
	return out
}

// The price effect of an applied discount in client-facing words. The chosen
// discount stores only { id, type, value, title }; percent/pax are the two CMS
// discount types (mirrors new-quote's discountSummary).
export const discountEffect = (discount) => {
	if (discount?.type === "percent" && discount.value) return `${discount.value}% off`
	if (discount?.type === "pax" && discount.value) return `1 in ${discount.value} free`
	return ""
}

// Full client-facing label for an applied discount: its name + effect, e.g.
// "Early Booking Offer - 10% off". Shared by the send screen (the show/hide
// list) and the published quote (summary + per-item lines) so the wording is
// identical everywhere.
export const discountChosenLabel = (discount) => {
	const title = (discount?.title || "").trim()
	const effect = discountEffect(discount)
	if (title && effect) return `${title} - ${effect}`
	return title || effect || "Discount"
}

/**
 * Attach client-facing "offer" labels to the hotel / round nodes they were
 * applied to, for the public quote page. `discountsChosen` lives on the source
 * basket item (it's what the agent ticked, and is already baked into the
 * price); this surfaces it as display-only lines on the matching node.
 *
 * `isHidden({ uid, id })` drops offers the agent unticked on the send screen.
 * Matching mirrors mergeHotelStays: rounds map 1:1 by uid, while hotels added
 * twice on the same check-in day (one merged card) pool their offers under the
 * same merge key. Pure - returns new node objects, leaving the input untouched
 * (so the internal builder preview, which doesn't pass a basket here, is
 * unaffected).
 */
export const attachItemDiscountsToNodes = (nodes, basket, isHidden = () => false) => {
	const items = Array.isArray(basket) ? basket : []
	const dayOf = (m) => (m ? moment(m).startOf("day").valueOf() : 0)

	const byUid = {}
	const byHotelKey = {}
	items.forEach((item) => {
		const labels = (Array.isArray(item.discountsChosen) ? item.discountsChosen : [])
			.filter((discount) => !isHidden({ uid: item.uid, id: discount.id }))
			.map((discount) => discountChosenLabel(discount))
		if (labels.length === 0) return
		if (item.type === "round") byUid[item.uid] = (byUid[item.uid] || []).concat(labels)
		else if (item.type === "hotel") {
			const key = `${item.cmsId || item.title}|${dayOf(item.checkInMillis)}`
			byHotelKey[key] = (byHotelKey[key] || []).concat(labels)
		}
	})

	return (nodes || []).map((node) => {
		const labels = node.kind === "round" ? byUid[node.key] : node.kind === "hotel" ? byHotelKey[`${node.cmsId || node.name}|${node.day}`] : null
		if (!labels || labels.length === 0) return node
		return { ...node, discounts: [...new Set(labels)] }
	})
}

/**
 * Flatten a basket into date-sorted timeline nodes (hotels, golf rounds, flight
 * legs and transfer legs). Pure: depends only on `basket`. Mirrors the order
 * rules the /mybooking timeline uses - flights pinned to the day's edges,
 * transfers bracketing the check-in / tee time they serve.
 */
export const buildItineraryNodes = (basket) => {
	// Resolve hotels/courses so transfers can name their endpoints + order
	// themselves around the right check-in / tee time.
	const hotelByUid = {}
	const courseByUid = {}
	basket.forEach((item) => {
		if (item.type === "hotel") hotelByUid[item.uid] = { title: item.title, millis: item.checkInMillis || 0, nights: Number(item.nights) || 0 }
		if (item.type === "round") {
			const times = (item.teeTimes || []).filter((tee) => tee > 0).sort((a, b) => a - b)
			const dateMillis = item.teeDate || times[0] || 0
			// `sortAnchor` mirrors the round node's own sort key (see the round branch
			// below) so transfers/shuttles bracket the round where it ACTUALLY sits: a
			// timed round at its first tee, a date-only round (time TBC) near the end of
			// its day. Bracketing the raw date instead would drop the return leg before
			// a date-only round (which is pinned past the day's hotel check-in).
			const dateOnly = times.length === 0 && Boolean(dateMillis)
			const sortAnchor = times[0] || (dateOnly ? moment(dateMillis).endOf("day").valueOf() - 2000 : 0)
			courseByUid[item.uid] = { title: item.title, millis: times[0] || item.teeDate || 0, sortAnchor }
		}
	})

	// Calendar day (start-of-day millis) a node is grouped under in the
	// timeline, distinct from its `sort` key (flights/transfers are pinned to
	// day edges for ordering, so their sort can fall in an adjacent day).
	const dayOf = (m) => (m ? moment(m).startOf("day").valueOf() : 0)

	const list = basket.flatMap((item) => {
		if (item.type === "hotel") {
			return [
				{
					key: item.uid,
					kind: "hotel",
					cmsId: item.cmsId || "",
					millis: item.checkInMillis || 0,
					sort: item.checkInMillis || Number.MAX_SAFE_INTEGER,
					day: dayOf(item.checkInMillis),
					name: item.title,
					image: item.image,
					checkInTime: item.checkInTime || "",
					nights: Number(item.nights) || 0,
					board: item.selectedBoard ? BOARD_TYPES[item.selectedBoard] || item.selectedBoard : "",
					rounds: item.selectedPackage?.rounds || 0,
					rooms: item.selectedRooms.map((room) => {
						const label = ROOM_TYPES[room.key] || room.key
						const qty = parseInt(room.qty) || 0
						return qty > 0 ? `${qty} x ${label}` : label
					}),
				},
			]
		}

		if (item.type === "round") {
			const times = (item.teeTimes || []).filter((tee) => tee > 0).sort((a, b) => a - b)
			// Prefer the round's explicit tee date; fall back to the earliest time
			// (older drafts had no separate date) so the card groups on the right day.
			const dateMillis = item.teeDate || times[0] || 0
			const anchor = times[0] || item.teeDate || 0
			// A date is set but no times yet - the card says "to be confirmed".
			const dateOnly = times.length === 0 && Boolean(dateMillis)
			// A timed round sorts at its first tee time. A date-only round (time
			// TBC) is pinned to the end of its day so it always sits AFTER that
			// day's hotel check-in (which anchors to the day start) rather than
			// before it - you've arrived, then there's a round to be scheduled.
			const sort = dateOnly ? moment(dateMillis).endOf("day").valueOf() - 2000 : anchor || Number.MAX_SAFE_INTEGER
			return [
				{
					key: item.uid,
					kind: "round",
					millis: anchor,
					sort,
					day: dayOf(dateMillis),
					name: item.title,
					image: item.image,
					teeTimes: times,
					dateOnly,
					// NB: the chosen green-fee band (item.chosenFeeType) deliberately
					// stays OFF the card - it's sales-agent pricing detail, not
					// something the client-facing itinerary should surface.
					buggies: buggyLabel(item.buggies),
				},
			]
		}

		if (item.type === "flight") {
			// One row per leg. Flights are anchored to the trip edges, not their
			// clock time: the outbound (first leg) is always the first thing on
			// its day - ahead of a same-day hotel check-in or round - and the
			// return (last leg of a return trip) is always the last, after any
			// round that day. A one-way is treated as an outbound. The card still
			// shows the real departure time; only the sort key is pinned.
			const legs = item.legs || []
			return legs
				.filter((leg) => leg.depMillis || leg.fromAirport || leg.toAirport)
				.map((leg, index) => {
					const isReturnLeg = legs.length > 1 && index === legs.length - 1
					let sort = leg.depMillis || Number.MAX_SAFE_INTEGER
					if (leg.depMillis) {
						// endOf day for the return; just before the day-start for the
						// outbound (the -1s also clears a midnight check-in and the
						// arrival transfer at check-in - 1ms).
						sort = isReturnLeg ? moment(leg.depMillis).endOf("day").valueOf() : moment(leg.depMillis).startOf("day").valueOf() - 1000
					}
					return {
						key: `${item.uid}-leg${index}`,
						kind: "flight",
						millis: leg.depMillis || 0,
						sort,
						day: dayOf(leg.depMillis),
						isReturn: isReturnLeg,
						carrier: leg.carrier,
						carrierCode: leg.carrierCode,
						from: leg.fromAirport,
						to: leg.toAirport,
						depMillis: leg.depMillis || 0,
						arrMillis: leg.arrMillis || 0,
						luggage: leg.luggage || [],
					}
				})
		}

		if (item.type === "transfer") {
			const hotel = hotelByUid[item.hotelUid]
			const hotelName = hotel?.title || "your hotel"
			const airportName = item.airportLabel || item.airport || "Airport"
			const out = []
			if (item.transferType === "airport" || item.transferType === "airport_golf") {
				// Airport transfers are a return trip: airport -> hotel on arrival
				// (after the outbound flight, before check-in) and hotel -> airport
				// on departure (after the stay, before the return flight).
				out.push({
					key: `${item.uid}-air-in`,
					kind: "transfer",
					// Sits just before check-in (arrival).
					sort: hotel?.millis ? hotel.millis - 1 : Number.MAX_SAFE_INTEGER,
					day: dayOf(hotel?.millis),
					text: `${airportName} to ${hotelName}`,
				})
				// Departure anchored to checkout (check-in + nights), at the end of
				// that day so it follows any round but precedes the return flight.
				const checkOutSort = hotel?.millis ? moment(hotel.millis).add(hotel.nights || 0, "days").endOf("day").valueOf() - 1 : 0
				out.push({
					key: `${item.uid}-air-out`,
					kind: "transfer",
					sort: checkOutSort || Number.MAX_SAFE_INTEGER,
					day: dayOf(hotel?.millis ? moment(hotel.millis).add(hotel.nights || 0, "days").valueOf() : 0),
					text: `${hotelName} to ${airportName}`,
				})
			}
			if (item.transferType === "golf" || item.transferType === "airport_golf") {
				// A golf transfer can serve several ticked courses (the bulk-add
				// flow); airport_golf and legacy golf transfers carry one courseUid.
				// Each course gets two legs - hotel -> course just before the tee
				// time, course -> hotel just after - bracketing that round's card.
				const golfCourseUids =
					item.transferType === "golf" && Array.isArray(item.courseUids) && item.courseUids.length ? item.courseUids : [item.courseUid]
				golfCourseUids.forEach((golfCourseUid, index) => {
					const golfCourse = courseByUid[golfCourseUid]
					const courseName = golfCourse?.title || "the course"
					out.push({
						key: `${item.uid}-golf-${index}-out`,
						kind: "transfer",
						sort: golfCourse?.sortAnchor ? golfCourse.sortAnchor - 1 : Number.MAX_SAFE_INTEGER,
						day: dayOf(golfCourse?.millis),
						text: `${hotelName} to ${courseName}`,
					})
					out.push({
						key: `${item.uid}-golf-${index}-back`,
						kind: "transfer",
						sort: golfCourse?.sortAnchor ? golfCourse.sortAnchor + 1 : Number.MAX_SAFE_INTEGER,
						day: dayOf(golfCourse?.millis),
						text: `${courseName} to ${hotelName}`,
					})
				})
			}
			if (item.transferType === "resort_shuttle") {
				// One shuttle serving several courses: a hotel <-> course pair per
				// chosen course, each bracketing that course's tee time, just like a
				// golf transfer but without adding a separate transfer per course.
				;(item.courseUids || []).forEach((courseUid, index) => {
					const shuttleCourse = courseByUid[courseUid]
					const shuttleCourseName = shuttleCourse?.title || "the course"
					out.push({
						key: `${item.uid}-shuttle-${index}-out`,
						kind: "transfer",
						kicker: "Resort Shuttles",
						sort: shuttleCourse?.sortAnchor ? shuttleCourse.sortAnchor - 1 : Number.MAX_SAFE_INTEGER,
						day: dayOf(shuttleCourse?.millis),
						text: `${hotelName} to ${shuttleCourseName}`,
					})
					out.push({
						key: `${item.uid}-shuttle-${index}-back`,
						kind: "transfer",
						kicker: "Resort Shuttles",
						sort: shuttleCourse?.sortAnchor ? shuttleCourse.sortAnchor + 1 : Number.MAX_SAFE_INTEGER,
						day: dayOf(shuttleCourse?.millis),
						text: `${shuttleCourseName} to ${hotelName}`,
					})
				})
			}
			return out
		}

		if (item.type === "eurotunnel") {
			// One node per crossing, anchored to the trip edges like the flight
			// legs: the outbound crossing leads its day (ahead of a same-day
			// check-in), the return crossing closes its day. The card shows the
			// crossing time when one is set.
			const crossings = [{ label: "Outbound crossing", millis: item.outboundMillis || 0, isReturn: false }]
			if ((item.tripType || "return") === "return") crossings.push({ label: "Return crossing", millis: item.returnMillis || 0, isReturn: true })
			return crossings.map((crossing, index) => ({
				key: `${item.uid}-crossing-${index}`,
				kind: "transfer",
				tunnel: true,
				kicker: "Euro Tunnel",
				millis: crossing.millis,
				sort: crossing.millis
					? crossing.isReturn
						? moment(crossing.millis).endOf("day").valueOf() + 500
						: moment(crossing.millis).startOf("day").valueOf() - 2000
					: Number.MAX_SAFE_INTEGER,
				day: dayOf(crossing.millis),
				text: `${crossing.label}${crossing.millis ? ` · ${moment(crossing.millis).format("HH:mm")}` : ""}`,
			}))
		}

		return []
	})
	return mergeHotelStays(list).sort((a, b) => a.sort - b.sort)
}

/**
 * Group the (already date-sorted) timeline nodes by calendar day. A plain date
 * label leads each day, with that day's cards beneath it. Undated items fall
 * into a trailing "Dates to be confirmed" group.
 */
export const groupNodesByDay = (nodes) => {
	const map = new Map()
	nodes.forEach((node) => {
		const key = node.day || 0
		if (!map.has(key)) map.set(key, [])
		map.get(key).push(node)
	})
	return Array.from(map.entries())
		.sort((a, b) => (a[0] === 0 ? 1 : b[0] === 0 ? -1 : a[0] - b[0]))
		.map(([day, items]) => ({ day, items }))
}

/**
 * Preview image that falls back to an image-placeholder icon when there's no
 * URL or the URL fails to load (CMS / supplier images aren't always present).
 */
export function PreviewImage({ src, className, iconSize }) {
	const [failed, setFailed] = useState(false)

	if (!src || failed) {
		return (
			<div className={`${className} is-placeholder`}>
				<ImagePlaceholderIcon size={iconSize} />
			</div>
		)
	}

	return <img className={className} src={src} alt="" onError={() => setFailed(true)} />
}

/**
 * Airline logo by IATA code, falling back to a plane icon when there's no code
 * or the logo fails to load.
 */
function CarrierLogo({ carrier, code }) {
	const [failed, setFailed] = useState(false)
	const url = carrierLogoUrl(code)

	if (!url || failed) {
		return (
			<div className="new-quote-carrier is-fallback" title={carrier || ""}>
				<Plane size={18} />
			</div>
		)
	}
	return <img className="new-quote-carrier" src={url} alt={carrier || code} onError={() => setFailed(true)} />
}

// Hotel-stay / golf-round card (image-topped, mirrors /mybooking).
function StayCard({ node }) {
	const isHotel = node.kind === "hotel"
	const typeLabel = isHotel ? "Hotel stay" : "Golf round"

	return (
		<div className="new-quote-node-card">
			<PreviewImage src={node.image} className="new-quote-node-image" iconSize={28} />

			<div className="new-quote-node-body">
				<span className="new-quote-node-kicker">{typeLabel}</span>
				<h4>{node.name || (isHotel ? "Hotel to be confirmed" : "Course to be confirmed")}</h4>

				{isHotel ? (
					<ul className="new-quote-node-facts">
						{node.checkInTime ? <li>Check in from {node.checkInTime}</li> : null}
						{node.nights ? (
							<li>
								Staying {node.nights} {node.nights === 1 ? "night" : "nights"}
								{node.millis ? `, check out ${moment(node.millis).add(node.nights, "days").format("ddd D MMM")}` : ""}
							</li>
						) : null}
						{node.board ? <li>{node.board}</li> : null}
						{node.rooms.length > 0 ? <li>{node.rooms.join(", ")}</li> : <li className="is-muted">Room type to be confirmed</li>}
					</ul>
				) : node.teeTimes.length > 0 || node.dateOnly || node.buggies ? (
					<ul className="new-quote-node-facts">
						{node.teeTimes.length > 0 ? (
							<li>Available tee times: {node.teeTimes.map((tee) => moment(tee).format("HH:mm")).join(", ")}</li>
						) : node.dateOnly ? (
							<li className="is-muted">Tee times to be confirmed</li>
						) : null}
						{node.buggies ? <li>{node.buggies}</li> : null}
					</ul>
				) : null}

				{/* Applied offers surfaced on the public quote (display-only). Absent on
				    the internal builder preview, which passes no discounts to its nodes. */}
				{node.discounts?.length > 0 ? (
					<ul className="new-quote-node-offers">
						{node.discounts.map((text, index) => (
							<li key={index}>
								<BadgePercent size={13} />
								<span>{text}</span>
							</li>
						))}
					</ul>
				) : null}
			</div>
		</div>
	)
}

// "LHR" -> "London Heathrow (LHR)". Strips the trailing country code and a
// trailing " Airport" so it reads as a place name; falls back to the bare code.
const airportLabel = (code) => {
	if (!code) return ""
	const raw = AIRPORTS[code]
	if (!raw) return code
	const name = raw.split(",")[0].replace(/\s+Airport$/i, "").trim()
	return name ? `${name} (${code})` : code
}

// Flight leg card: a full-name route line, then the carrier logo + times /
// airport-code / duration row, then date + luggage / golf-bag facts.
function FlightCard({ node }) {
	const duration = flightDuration(node.depMillis, node.arrMillis)

	return (
		<div className="new-quote-node-card is-flight">
			<span className="new-quote-node-kicker">{node.isReturn ? "Return flight" : "Outbound flight"}</span>

			{/* Logo left, two rows right: carrier name then the full-name route. */}
			<div className="new-quote-flight-head">
				<CarrierLogo carrier={node.carrier} code={node.carrierCode} />
				<div className="new-quote-flight-head-meta">
					<strong className="new-quote-flight-carrier-name">{node.carrier || node.carrierCode || "Airline to be confirmed"}</strong>
					{node.from || node.to ? (
						<span className="new-quote-flight-route">
							{airportLabel(node.from) || "—"}
							<span className="new-quote-flight-route-arrow" aria-hidden="true">&rarr;</span>
							{airportLabel(node.to) || "—"}
						</span>
					) : null}
				</div>
			</div>

			<div className="new-quote-flight">
				<div className="new-quote-flight-point">
					<strong>{node.depMillis ? moment(node.depMillis).format("HH:mm") : "--:--"}</strong>
					<span>{node.from || "—"}</span>
				</div>

				<div className="new-quote-flight-mid">
					<small>{duration || " "}</small>
					<span className="new-quote-flight-line">
						{/* Filled plane (from the bookings flight card) - stays crisp at this small size, unlike a stroke icon. */}
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15.755 14" aria-hidden="true">
							<path
								d="M13.152-7a3.4,3.4,0,0,1,1.723.533q.9.533.9,1.217t-.9,1.217a3.4,3.4,0,0,1-1.723.533H10.035L7.164,1.531a.428.428,0,0,1-.383.219h-1.8a.393.393,0,0,1-.342-.178A.419.419,0,0,1,4.566,1.2l1.34-4.7H3.09L1.914-1.914a.441.441,0,0,1-.355.164H.465a.4.4,0,0,1-.342-.164.509.509,0,0,1-.1-.383L.9-5.25.027-8.2a.509.509,0,0,1,.1-.383A.4.4,0,0,1,.465-8.75H1.559a.441.441,0,0,1,.355.164L3.09-7H5.906l-1.34-4.7a.433.433,0,0,1-.014-.191.363.363,0,0,1,.082-.191.472.472,0,0,1,.15-.123.423.423,0,0,1,.191-.041h1.8a.428.428,0,0,1,.383.219L10.035-7Z"
								transform="translate(-0.022 12.25)"
								fill="currentColor"
							/>
						</svg>
					</span>
				</div>

				<div className="new-quote-flight-point">
					<strong>{node.arrMillis ? moment(node.arrMillis).format("HH:mm") : "--:--"}</strong>
					<span>{node.to || "—"}</span>
				</div>
			</div>

			{node.luggage.length > 0 ? (
				<ul className="new-quote-node-facts">
					<li>Luggage included: {node.luggage.map((key) => LUGGAGE_OPTIONS[key] || key).join(", ")}</li>
				</ul>
			) : null}
		</div>
	)
}

// Slim transfer card (no image).
function TransferCard({ node }) {
	return (
		<div className="new-quote-node-card is-transfer">
			<span className="new-quote-node-kicker">{node.kicker || "Transfer"}</span>
			<p className="new-quote-transfer-text">{node.text}</p>
		</div>
	)
}

/**
 * A single timeline card, mirroring the /mybooking client itinerary: a rail
 * with an icon dot, then an image-topped card with the key facts.
 */
export function TimelineNode({ node, isLast }) {
	const dotIcon =
		node.kind === "hotel" ? <BedDouble size={16} /> : node.kind === "round" ? <Flag size={16} /> : node.kind === "flight" ? <Plane size={16} /> : node.tunnel ? <TrainFront size={16} /> : <Car size={16} />

	return (
		<div className={["new-quote-node", `is-${node.kind}`].join(" ")}>
			<div className="new-quote-node-rail">
				<span className={["new-quote-node-dot", `is-${node.kind}`].join(" ")}>{dotIcon}</span>
				{!isLast && <span className="new-quote-node-line" />}
			</div>

			{node.kind === "flight" ? <FlightCard node={node} /> : node.kind === "transfer" ? <TransferCard node={node} /> : <StayCard node={node} />}
		</div>
	)
}

/**
 * The full day-grouped itinerary timeline. Pass pre-built `nodes`
 * (buildItineraryNodes) - the builder passes its live nodes, the public page
 * passes the nodes persisted on the published quote.
 */
export function ItineraryTimeline({ nodes }) {
	const dayGroups = groupNodesByDay(nodes)

	return (
		<div className="new-quote-timeline">
			{dayGroups.map((group) => (
				<div className="new-quote-day-group" key={group.day}>
					<p className="new-quote-day-label">{group.day ? moment(group.day).format("dddd Do MMMM, YYYY") : "Dates to be confirmed"}</p>
					{group.items.map((node, index) => (
						<TimelineNode key={node.key} node={node} isLast={index === group.items.length - 1} />
					))}
				</div>
			))}
		</div>
	)
}
